/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// You can delete this file if you're not using it

import React, { ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { GlobalProvider } from './src/components/global-context';
import { WrapPageElementBrowserArgs, ShouldUpdateScrollArgs } from 'gatsby';
import {
  initializeGlobalAnalytics,
  resetScrollDepthTracking,
  trackEvent,
} from './src/lib/analytics';

type FundraiseUpEventDetails = {
  livemode?: boolean;
  campaign?: { id?: string; name?: string };
  element?: { type?: string; name?: string };
  donation?: {
    id?: string;
    amount?: number;
    currency?: string;
    recurring?: boolean;
    frequency?: string;
  };
  designation?: { id?: string; name?: string };
};

declare global {
  interface Window {
    FundraiseUp?: {
      on: (event: string, callback: (details: FundraiseUpEventDetails) => void) => void;
    };
  }
}

type MetaPixelParameters = Record<string, string | number | boolean | string[] | undefined>;

const cleanMetaPixelParameters = (
  parameters: MetaPixelParameters
): MetaPixelParameters => (
  Object.fromEntries(
    Object.entries(parameters)
      .filter(([, value]) => value !== undefined && value !== "")
  ) as MetaPixelParameters
);

const trackMetaPixelEvent = (
  event: string,
  parameters: MetaPixelParameters = {}
): void => {
  if (typeof window === "undefined" || typeof (window as any).fbq !== "function") return;

  (window as any).fbq("track", event, cleanMetaPixelParameters(parameters));
};

export const onClientEntry = () => {
  initializeGlobalAnalytics();

  if (!window.FundraiseUp) return;

  window.FundraiseUp.on('checkoutOpen', (details) => {
    trackEvent('begin_checkout', {
      campaign_id: details.campaign?.id,
      campaign_name: details.campaign?.name,
      checkout_element: details.element?.name || details.element?.type,
      live_mode: details.livemode,
    });
  });

  window.FundraiseUp.on('donationComplete', (details) => {
    const donationAmount = details.donation?.amount;
    const donationCurrency = details.donation?.currency;
    const campaignId = details.campaign?.id;
    const campaignName = details.campaign?.name;
    const donationFrequency = details.donation?.frequency;

    trackEvent('donation_complete', {
      value: donationAmount,
      currency: donationCurrency,
      recurring: details.donation?.recurring,
      donation_frequency: donationFrequency,
      campaign_id: campaignId,
      campaign_name: campaignName,
      designation_id: details.designation?.id,
      designation_name: details.designation?.name,
      live_mode: details.livemode,
    });

    trackMetaPixelEvent('Donate', {
      value: donationAmount,
      currency: donationCurrency,
      content_category: 'Fundraise Up',
      content_ids: campaignId ? [campaignId] : undefined,
      content_name: campaignName,
      content_type: details.donation?.recurring ? 'Recurring Donation' : 'One-time Donation',
      donation_id: details.donation?.id,
      donation_frequency: donationFrequency,
      designation_id: details.designation?.id,
      designation_name: details.designation?.name,
      fundraiseup_campaign_id: campaignId,
      fundraiseup_campaign_name: campaignName,
    });
  });
};

export const wrapPageElement = ({ element }: WrapPageElementBrowserArgs): ReactNode => (
  <GlobalProvider>
    <AnimatePresence mode="sync">
      {element}
    </AnimatePresence>
  </GlobalProvider>
);

export const shouldUpdateScroll = ({
  routerProps: { location },
  prevRouterProps,
  getSavedScrollPosition,
}: ShouldUpdateScrollArgs): boolean => {

  const transitionDelay = 100;
  window.history.scrollRestoration = "manual";

  // Check if the navigation is within the same page (only parameters changed)
  if (prevRouterProps && location.pathname === prevRouterProps.location.pathname) {
    // Maintain the current scroll position for in-page navigations (e.g., filter changes)
    return false;
  }

  const currentPosition = getSavedScrollPosition(location) || [0, 0];

  setTimeout(() => {
    window.scrollTo(...currentPosition);
  }, transitionDelay);

  return false;
};

// tracking code update on route change
export const onRouteUpdate = ({ location, prevLocation }) => {
  resetScrollDepthTracking();

  // Skip initial SSR pageview for fbq to avoid double-count
  if (!prevLocation) return;

  // Meta Pixel SPA pageview
  if (typeof window !== "undefined" && typeof (window as any).fbq === "function") {
    (window as any).fbq("track", "PageView");
  }

  // Pardot (Account Engagement) manual SPA pageview
  // Using Tracking & Consent API: queue a 'sendPageView' command
  // if (typeof window !== "undefined" && (window as any).pardot) {
  //   (window as any).pardot.push([
  //     "sendPageView",
  //     {
  //       title: document.title,
  //       location: window.location.href,
  //       referrer: document.referrer,
  //     },
  //   ]);
  // }

  // Hotjar manual state change (only needed if you choose manual mode)
  // if (typeof window !== "undefined" && typeof (window as any).hj === "function") {
  //   (window as any).hj("stateChange", location.pathname + location.search);
  // }
};
